import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';

import {isNotNil} from 'ramda';

import {
  UserComplaintRequestBody,
  UserComplaintResponse,
  CreateRelativeFilterRequestBody,
  RelativeFilterResponseBody,
  SetRelativeFilterDefaultRequestBody,
  UpdateRelativeFilterRequestBody,
  ReorderRelativeFiltersRequestBody,
} from '../types/api/api';
import {DrillDownApiArg, DrillDownResponse} from '../types/api/drillDown';
import {fixAlfaBugsInDrillDownResponse} from '../utils/fixAlfaBugsInDrillDownResponse';

export const omneticApi = createApi({
  reducerPath: 'omnetic',
  tagTypes: ['RelativeFilter'],
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.OMNETIC_API_URL,
  }),
  endpoints: (build) => ({
    drillDown: build.query<DrillDownResponse, DrillDownApiArg>({
      query: (queryArg) => ({
        url: '/dms/v2/sourcing/drill-down',
        method: 'POST',
        body: queryArg,
        headers: {
          'X-Workspace': 'price-report-backend',
          'accept-language': queryArg.lang,
        },
      }),
      // TODO T20-30972 - fix response on BE
      transformResponse: (response: DrillDownResponse, meta, arg: DrillDownApiArg) =>
        fixAlfaBugsInDrillDownResponse(
          arg.levels.map((level) => level.level),
          response
        ),
    }),
    sourcingDrillDownGlobal: build.query<DrillDownResponse, DrillDownApiArg>({
      query: (queryArg) => ({
        url: '/dms/v2/sourcing/drill-down-global',
        method: 'POST',
        body: queryArg,
        headers: {
          'X-Workspace': 'price-report-backend',
          'accept-language': queryArg.lang,
        },
      }),
      // TODO T20-30972 - fix response on BE
      transformResponse: (response: DrillDownResponse, meta, arg: DrillDownApiArg) =>
        fixAlfaBugsInDrillDownResponse(
          arg.levels.map((level) => level.level),
          response
        ),
    }),
    userComplaint: build.mutation<UserComplaintResponse, UserComplaintRequestBody>({
      query: (queryArg) => ({
        url: '/dms/v1/sales/price-report/user-complaint',
        method: 'POST',
        body: queryArg,
      }),
    }),
    createRelativeFilter: build.mutation<
      {id: string},
      {customerId: string} & CreateRelativeFilterRequestBody
    >({
      query: ({customerId, ...body}) => ({
        url: `/dms/v1/sourcing/relative-filter`,
        method: 'POST',
        body,
        headers: {'X-Tenant': customerId},
      }),
      invalidatesTags: [{type: 'RelativeFilter', id: 'LIST'}],
    }),
    getRelativeFilters: build.query<RelativeFilterResponseBody[], {customerId: string}>({
      query: ({customerId}) => ({
        url: `/dms/v1/sourcing/relative-filter`,
        headers: {'X-Tenant': customerId},
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({id}) => ({type: 'RelativeFilter' as const, id})),
              {type: 'RelativeFilter' as const, id: 'LIST'},
            ]
          : [{type: 'RelativeFilter' as const, id: 'LIST'}],
    }),
    deleteRelativeFilter: build.mutation<void, {id: string; customerId: string}>({
      query: ({id, customerId}) => ({
        url: `/dms/v1/sourcing/relative-filter/${id}`,
        method: 'DELETE',
        headers: {'X-Tenant': customerId},
      }),
      invalidatesTags: (_r, _e, {id}) => [
        {type: 'RelativeFilter', id},
        {type: 'RelativeFilter', id: 'LIST'},
      ],
    }),
    updateRelativeFilter: build.mutation<
      void,
      {id: string; customerId: string} & (
        | UpdateRelativeFilterRequestBody
        | SetRelativeFilterDefaultRequestBody
      )
    >({
      query: ({id, customerId, ...body}) => ({
        url: `/dms/v1/sourcing/relative-filter/${id}`,
        method: 'PUT',
        body,
        headers: {'X-Tenant': customerId},
      }),
      invalidatesTags: (_r, _e, {id}) => [
        {type: 'RelativeFilter', id},
        {type: 'RelativeFilter', id: 'LIST'},
      ],
    }),
    reorderRelativeFilters: build.mutation<
      void,
      {customerId: string} & ReorderRelativeFiltersRequestBody
    >({
      query: ({customerId, ...body}) => ({
        url: `/dms/v1/sourcing/relative-filter/reorder`,
        method: 'PUT',
        body,
        headers: {'X-Tenant': customerId},
      }),
      onQueryStarted({customerId, items}, {dispatch, queryFulfilled}) {
        const patchResult = dispatch(
          omneticApi.util.updateQueryData('getRelativeFilters', {customerId}, (draft) => {
            items.forEach(({id, position}) => {
              const preset = draft.find((item) => item.id === id);
              if (isNotNil(preset)) {
                preset.position = position;
              }
            });
            draft.sort((left, right) => left.position - right.position);
          })
        );
        queryFulfilled.catch(patchResult.undo);
      },
      invalidatesTags: [{type: 'RelativeFilter', id: 'LIST'}],
    }),
  }),
});

export const {
  useDrillDownQuery,
  useSourcingDrillDownGlobalQuery,
  useUserComplaintMutation,
  useCreateRelativeFilterMutation,
  useGetRelativeFiltersQuery,
  useDeleteRelativeFilterMutation,
  useUpdateRelativeFilterMutation,
  useReorderRelativeFiltersMutation,
} = omneticApi;
